'use client';

import { MONO_FONT } from '@/theme';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ScrollCue from '@/components/ScrollCue';

export default function ArticleHeader({ article }) {
  const { category, date, readTime, title, excerpt, author, image } = article;

  return (
    <Box
      component="header"
      data-nav-theme="dark"
      sx={{
        position: 'relative',
        bgcolor: 'grey.950',
        borderBottom: '1px solid',
        borderColor: 'grey.900',
        overflow: 'hidden',
      }}
    >
      {/* Desktop hero image — absolute right half */}
      {image && (
        <Box
          component="img"
          src={image.src}
          alt={image.alt}
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: { md: '44%', lg: '46%' },
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}

      {/* Gradiente que funde la imagen con el área de texto (transición suave) */}
      {image && (
        <Box
          aria-hidden
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            width: { md: '56%', lg: '58%' },
            background:
              'linear-gradient(to right, #0a0f0e 0%, #0a0f0e 24%, rgba(10,15,14,0) 72%)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}

      {/* Text content */}
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: '20px', sm: '32px' },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: '100%', md: image ? '54%' : '800px' },
            pt: '96px',
            pb: { xs: '72px', md: '96px' },
          }}
        >
          {/* Meta bar */}
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              alignItems: 'center',
              fontFamily: MONO_FONT,
              fontSize: '11px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              mb: 4,
            }}
          >
            <Box
              component="span"
              sx={{ color: 'primary.glow', fontWeight: 600 }}
            >
              {category}
            </Box>
            <Box component="span" sx={{ color: 'grey.700' }}>
              /
            </Box>
            <Box component="span" sx={{ color: 'text.muted' }}>
              {date}
            </Box>
            <Box component="span" sx={{ color: 'grey.700' }}>
              /
            </Box>
            <Box component="span" sx={{ color: 'text.muted' }}>
              {readTime}
            </Box>
          </Box>

          {/* Mobile image — full-bleed break between eyebrow and title */}
          {image && (
            <Box
              component="figure"
              sx={{
                m: 0,
                mx: { xs: '-20px', sm: '-32px' },
                mb: 5,
                display: { xs: 'block', md: 'none' },
              }}
            >
              <Box
                component="img"
                src={image.src}
                alt={image.alt}
                sx={{
                  width: '100%',
                  aspectRatio: '16 / 10',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              {image.caption && (
                <Box
                  component="figcaption"
                  sx={{
                    px: { xs: '20px', sm: '32px' },
                    py: '10px',
                    fontFamily: MONO_FONT,
                    fontSize: '11px',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.muted',
                  }}
                >
                  {image.caption}
                </Box>
              )}
            </Box>
          )}

          {/* Title */}
          <Typography
            component="h1"
            sx={{
              fontSize: 'clamp(28px, 3.5vw, 52px)',
              fontWeight: 400,
              letterSpacing: '-0.028em',
              lineHeight: { xs: 1.18, md: 1.08 },
              color: 'common.white',
              mb: 3,
              '& .accent': { color: 'primary.glow' },
            }}
            dangerouslySetInnerHTML={{ __html: title }}
          />

          {/* Excerpt */}
          {excerpt && (
            <Typography
              sx={{
                fontSize: 'clamp(15px, 1.3vw, 18px)',
                lineHeight: 1.6,
                color: 'text.muted',
                mb: 5,
              }}
            >
              {excerpt}
            </Typography>
          )}

          {/* Byline */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              pt: 4,
              borderTop: '1px solid',
              borderColor: 'grey.800',
            }}
          >
            {author.avatar ? (
              <Box
                component="img"
                src={author.avatar}
                alt={author.name}
                width={38}
                height={38}
                sx={{
                  width: { xs: 30, md: 38 },
                  height: { xs: 30, md: 38 },
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'block',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: 30, md: 38 },
                  height: { xs: 30, md: 38 },
                  borderRadius: '50%',
                  bgcolor: 'primary.darker',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: MONO_FONT,
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'primary.glow',
                  flexShrink: 0,
                  letterSpacing: '0.04em',
                }}
              >
                {author.initials}
              </Box>
            )}
            <Box>
              <Box
                {...(author.url && { component: 'a', href: author.url, rel: 'author' })}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '24px',
                  fontSize: { xs: '13px', md: '14px' },
                  fontWeight: 600,
                  color: 'common.white',
                  lineHeight: 1.3,
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.glow', textDecoration: 'underline', textUnderlineOffset: '3px' },
                }}
              >
                {author.name}
              </Box>
              <Box
                sx={{
                  fontFamily: MONO_FONT,
                  fontSize: { xs: '10px', md: '11px' },
                  letterSpacing: '0.06em',
                  color: 'text.muted',
                  textTransform: 'uppercase',
                  mt: '2px',
                }}
              >
                {author.role}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Flecha al contenido — centro inferior del hero */}
      <ScrollCue target="contenido" label="Ir al contenido del artículo" />
    </Box>
  );
}
